import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database, UserPlan } from "@/types/supabase";

type Client = SupabaseClient<Database>;

export interface AdminUserRow {
  id: string;
  email: string | null;
  display_name: string | null;
  role: string;
  billing_mode: string;
  plan: UserPlan;
  created_at: string;
}

export interface WhitelistEntry {
  id: string;
  email: string;
  created_at: string;
}

export async function fetchAdminUsers(client: Client): Promise<AdminUserRow[]> {
  const { data, error } = await client
    .from("profiles")
    .select("id, email, display_name, role, billing_mode, plan, created_at")
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as AdminUserRow[];
}

export async function updateUserPlan(
  client: Client,
  userId: string,
  plan: UserPlan,
): Promise<void> {
  const { error } = await client
    .from("profiles")
    .update({ plan })
    .eq("id", userId);
  if (error) throw error;
}

export async function fetchWhitelist(client: Client): Promise<WhitelistEntry[]> {
  const { data, error } = await client
    .from("access_whitelist")
    .select("id, email, created_at")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export async function addToWhitelist(
  client: Client,
  email: string,
  createdBy: string,
): Promise<void> {
  const { error } = await client
    .from("access_whitelist")
    .insert({ email: email.toLowerCase().trim(), created_by: createdBy });
  if (error) throw error;
}

export async function removeFromWhitelist(
  client: Client,
  id: string,
): Promise<void> {
  const { error } = await client
    .from("access_whitelist")
    .delete()
    .eq("id", id);
  if (error) throw error;
}

export async function deleteUser(
  client: Client,
  userId: string,
): Promise<void> {
  const { error } = await client.functions.invoke("admin-delete-user", {
    method: "POST",
    body: { user_id: userId },
  });
  if (error) throw error;
}
